import { graphql, readInlineData } from "react-relay";

import type { helpersTypeMenuQuery$key } from "scmp-app/queries/__generated__/helpersTypeMenuQuery.graphql";
import type { ContentTypeEnum } from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";

import { type TypeOption, TypeOptions } from "./const";

export type TypeOptionWithCount = TypeOption & {
  count: string;
};

const ALL_CONTENT_TYPES: ContentTypeEnum[] = ["ARTICLE", "VIDEO", "GALLERY"];

export function getTypeOptionsWithCount(
  fragmentReference: helpersTypeMenuQuery$key,
): TypeOptionWithCount[] {
  const data = readInlineData(
    graphql`
      fragment helpersTypeMenuQuery on Query @inline {
        articleSearchV2(
          filter: {
            query: $query
            sectionIds: $sectionIds
            articleTypes: $articleTypeIds
            paywallTypeIds: $paywallTypeIds
            contentTypes: $contentTypes
            startDate: $startDate
            endDate: $endDate
          }
          orderBy: $orderBy
          first: $first
          after: $after
        ) {
          aggregations {
            articleTypeId {
              buckets {
                doc_count
                key
              }
            }
            contentType {
              buckets {
                doc_count
                key
              }
            }
          }
        }
      }
    `,
    fragmentReference,
  );

  const articleTypeBuckets = data.articleSearchV2?.aggregations?.articleTypeId?.buckets ?? [];
  const contentTypeBuckets = data.articleSearchV2?.aggregations?.contentType?.buckets ?? [];

  const articleTypeCountMap = new Map(
    articleTypeBuckets.map(bucket => [bucket.key, bucket.doc_count || 0]),
  );
  const contentTypeCountMap = new Map(
    contentTypeBuckets.map(bucket => [bucket.key.toLowerCase(), bucket.doc_count || 0]),
  );

  const baseOptions: TypeOption[] = [
    { isContentType: true, text: "All", value: [...ALL_CONTENT_TYPES] },
    ...TypeOptions,
  ];

  return baseOptions.map(option => {
    let count: number = 0;

    if (option.text === "All") {
      count = 0;
    } else if (option.isContentType) {
      count = (option.value as ContentTypeEnum[]).reduce(
        (sum, value) => sum + (contentTypeCountMap.get(value.toLowerCase()) ?? 0),
        0,
      );
    } else {
      count = (option.value as string[]).reduce(
        (sum, value) => sum + (articleTypeCountMap.get(value) ?? 0),
        0,
      );
    }

    return { ...option, count: count > 0 ? count.toLocaleString() : "" };
  });
}
